/**
 * NotFound (404) Page Component
 * Displays a friendly, themed error page when users navigate to non-existent routes
 * Incorporates heat pump visuals to stay consistent with project branding
 * @module NotFound
 */

import React, { useMemo } from 'react';
import { Button, Typography, Space, Card } from 'antd';
import { HomeOutlined, ExperimentOutlined, ArrowLeftOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PagesWrapper from '../../PagesWrapper';
import styles from './NotFound.module.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Animated Heat Pump SVG with "frozen" state to represent the 404 error
 * The heat pump appears to be searching/confused
 */
const FrozenHeatPump = () => (
  <svg 
    width="280" 
    height="200" 
    viewBox="0 0 280 200" 
    xmlns="http://www.w3.org/2000/svg"
    className={styles.heatPumpSvg}
  >
    <defs>
      {/* Snowflake gradient for frozen effect */}
      <linearGradient id="frozenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e0f2fe" />
        <stop offset="100%" stopColor="#bae6fd" />
      </linearGradient>
      {/* Ice crystal pattern */}
      <pattern id="icePattern" width="20" height="20" patternUnits="userSpaceOnUse">
        <circle cx="10" cy="10" r="1" fill="rgba(147, 197, 253, 0.3)" />
      </pattern>
    </defs>

    {/* Background glow */}
    <ellipse cx="140" cy="180" rx="100" ry="15" fill="rgba(147, 197, 253, 0.2)" />

    {/* Heat pump unit body */}
    <g transform="translate(70, 30)">
      {/* Main body with frost effect */}
      <rect x="0" y="20" width="140" height="120" rx="8" 
            fill="url(#frozenGradient)" stroke="#7dd3fc" strokeWidth="3"/>
      <rect x="0" y="20" width="140" height="120" rx="8" fill="url(#icePattern)" />
      
      {/* Top grille section */}
      <rect x="10" y="30" width="120" height="60" rx="4" fill="#fff" stroke="#93c5fd" strokeWidth="2"/>
      
      {/* Grille lines */}
      <line x1="20" y1="42" x2="120" y2="42" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
      <line x1="20" y1="54" x2="120" y2="54" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
      <line x1="20" y1="66" x2="120" y2="66" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
      <line x1="20" y1="78" x2="120" y2="78" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round"/>
      
      {/* Confused fan - wobbling animation */}
      <g className={styles.confusedFan}>
        <circle cx="70" cy="60" r="20" fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 2"/>
        <ellipse cx="70" cy="45" rx="4" ry="12" fill="#3b82f6" opacity="0.6"/>
        <ellipse cx="70" cy="75" rx="4" ry="12" fill="#3b82f6" opacity="0.6"/>
        <ellipse cx="55" cy="60" rx="12" ry="4" fill="#3b82f6" opacity="0.6"/>
        <ellipse cx="85" cy="60" rx="12" ry="4" fill="#3b82f6" opacity="0.6"/>
        <circle cx="70" cy="60" r="5" fill="#1d4ed8"/>
      </g>
      
      {/* Control panel */}
      <rect x="20" y="100" width="100" height="30" rx="4" fill="#f0f9ff" stroke="#93c5fd" strokeWidth="1.5"/>
      
      {/* 404 display on panel */}
      <text x="70" y="120" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#ef4444" fontFamily="monospace">
        ERR: 404
      </text>
      
      {/* Status LEDs */}
      <circle cx="30" cy="108" r="3" fill="#fbbf24" className={styles.blinkingLed}/>
      <circle cx="110" cy="108" r="3" fill="#ef4444" className={styles.blinkingLedFast}/>
      
      {/* Icicles hanging from the unit */}
      <polygon points="15,20 18,5 21,20" fill="#bfdbfe" opacity="0.8"/>
      <polygon points="35,20 37,8 39,20" fill="#bfdbfe" opacity="0.8"/>
      <polygon points="55,20 58,3 61,20" fill="#bfdbfe" opacity="0.8"/>
      <polygon points="80,20 82,10 84,20" fill="#bfdbfe" opacity="0.8"/>
      <polygon points="100,20 103,6 106,20" fill="#bfdbfe" opacity="0.8"/>
      <polygon points="120,20 122,12 124,20" fill="#bfdbfe" opacity="0.8"/>
    </g>

    {/* Question marks floating */}
    <g className={styles.floatingQuestion}>
      <text x="50" y="60" fontSize="28" fill="#60a5fa" fontWeight="bold">?</text>
    </g>
    <g className={styles.floatingQuestion2}>
      <text x="220" y="50" fontSize="22" fill="#93c5fd" fontWeight="bold">?</text>
    </g>
    
    {/* Snowflakes */}
    <g className={styles.snowflake1}>
      <text x="30" y="100" fontSize="16" fill="#bfdbfe">❄</text>
    </g>
    <g className={styles.snowflake2}>
      <text x="240" y="90" fontSize="14" fill="#bfdbfe">❄</text>
    </g>
    <g className={styles.snowflake3}>
      <text x="255" y="140" fontSize="12" fill="#dbeafe">❄</text>
    </g>
  </svg>
);

/**
 * Suggested navigation links based on project sections
 */
const SuggestedLinks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const suggestions = [
    {
      icon: <HomeOutlined />,
      title: t('notFound.suggestions.home', 'Home'),
      description: t('notFound.suggestions.homeDesc', 'Return to the main page'),
      path: '/'
    },
    {
      icon: <ExperimentOutlined />,
      title: t('notFound.suggestions.simulations', 'Simulations'),
      description: t('notFound.suggestions.simulationsDesc', 'Explore interactive heat pump simulations'),
      path: '/simulations'
    },
    {
      icon: <InfoCircleOutlined />,
      title: t('notFound.suggestions.about', 'About'),
      description: t('notFound.suggestions.aboutDesc', 'Learn more about us'),
      path: '/about'
    },
    {
      icon: <MailOutlined />,
      title: t('notFound.suggestions.contact', 'Contact'),
      description: t('notFound.suggestions.contactDesc', 'Get in touch with our team'),
      path: '/contact'
    }
  ];

  return (
    <div className={styles.suggestionsGrid}>
      {suggestions.map((item, index) => (
        <Card 
          key={index}
          className={styles.suggestionCard}
          hoverable
          onClick={() => navigate(item.path)}
        >
          <div className={styles.suggestionIcon}>{item.icon}</div>
          <Text strong className={styles.suggestionTitle}>{item.title}</Text>
          <Text type="secondary" className={styles.suggestionDesc}>{item.description}</Text>
        </Card>
      ))}
    </div>
  );
};

/**
 * NotFound Page Component
 * Displays when users navigate to a route that doesn't exist
 */
const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Generate a fun heat-pump related message
  const funMessages = useMemo(() => [
    t('notFound.funMessages.0', "Looks like this page went on vacation to a warmer climate!"),
    t('notFound.funMessages.1', "Our heat pump searched everywhere, but couldn't find this page."),
    t('notFound.funMessages.2', "This page seems to have lost its thermal equilibrium."),
    t('notFound.funMessages.3', "404: Page not found. Even our best installers couldn't locate it!"),
    t('notFound.funMessages.4', "The coefficient of finding this page is currently zero."),
  ], [t]);

  const randomMessage = useMemo(() => 
    funMessages[Math.floor(Math.random() * funMessages.length)], 
    [funMessages]
  );

  return (
    <PagesWrapper>
      <div className={styles.notFoundContainer}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            {/* Animated Heat Pump Visual */}
            <div className={styles.visualContainer}>
              <FrozenHeatPump />
            </div>

            {/* Main Content */}
            <div className={styles.textContent}>
              <Title level={1} className={styles.errorCode}>404</Title>
              <Title level={2} className={styles.errorTitle}>
                {t('notFound.title', 'Page Not Found')}
              </Title>
              <Paragraph className={styles.errorMessage}>
                {randomMessage}
              </Paragraph>
              <Paragraph type="secondary" className={styles.pathInfo}>
                {t('notFound.pathInfo', 'The requested path')} <code className={styles.pathCode}>{location.pathname}</code> {t('notFound.doesNotExist', "doesn't exist in our system.")}
              </Paragraph>

              {/* Action Buttons */}
              <Space size="middle" className={styles.actionButtons}>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<HomeOutlined />}
                  onClick={() => navigate('/')}
                  className={styles.primaryButton}
                >
                  {t('notFound.goHome', 'Go to Home')}
                </Button>
                <Button 
                  size="large" 
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  className={styles.secondaryButton}
                >
                  {t('notFound.goBack', 'Go Back')}
                </Button>
              </Space>
            </div>
          </div>
        </div>

        {/* Bottom Sections - Side by Side */}
        <div className={styles.bottomSections}>
          {/* Helpful Suggestions Section */}
          <div className={styles.suggestionsSection}>
            <Title level={3} className={styles.suggestionsTitle}>
              {t('notFound.suggestionsTitle', 'Where would you like to go?')}
            </Title>
            <Paragraph type="secondary" className={styles.suggestionsSubtitle}>
              {t('notFound.suggestionsSubtitle', 'Explore our interactive learning platform for heat pump professionals')}
            </Paragraph>
            <SuggestedLinks />
          </div>

          {/* Fun Fact Section */}
          <div className={styles.funFactSection}>
            <Card className={styles.funFactCard}>
              <div className={styles.funFactContent}>
                <span className={styles.funFactIcon}>💡</span>
                <div>
                  <Text strong>{t('notFound.didYouKnow', 'Did you know?')}</Text>
                  <Paragraph className={styles.funFactText}>
                    {t('notFound.funFact', 'A well-installed heat pump can achieve a Coefficient of Performance (COP) of 4 or higher, meaning for every 1 kW of electricity used, it can produce 4 kW of heat. That\'s 400% efficient compared to traditional heating!')}
                  </Paragraph>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </PagesWrapper>
  );
};

export default NotFound;
